import { Edit, HelpOutline, PhotoCamera } from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  IconButton,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { API_URL } from "../../../config/apiConfig";
import { formatApiError } from "../../../utils/formatApiError";
import {
  deleteProfilePicture,
  uploadProfilePicture,
} from "../../../services/profilePictureService";
import {
  getMyProfile,
  ProfileData,
  updateProfile,
  UpdateProfileRequest,
} from "../../../services/profileService";
import {
  getNationalLengthForCountry,
  inferCountryCodeFromPhone,
  requiresStrictNationalLength,
  sanitizePhoneDigits,
  stripDialCodeFromPhone,
  toE164,
  trimPhoneToNationalLength,
} from "../../../utils/CountryPhoneUtils";
import {
  alphaOnly,
  isAlphaText,
  isValidEmail,
  validationMessages,
} from "../../../utils/InputValidation";
import { type Country } from "../../../utils/countries";
import CountryCodeSelect from "../../common/CountryCodeSelect";

type ProfileFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  city: string;
  country: string;
  addressLine1: string;
};

type FieldErrors = Partial<Record<keyof ProfileFormData, string>>;

// Helper function to construct full image URL
const getFullImageUrl = (imagePath: string | null | undefined): string => {
  if (!imagePath)
    return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect width='120' height='120' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='14' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";

  // If it's already a full URL, return as is
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }

  // If it's a relative path, prepend the base URL without /api
  const baseUrl = API_URL.replace("/api", "");
  return `${baseUrl}${imagePath.startsWith("/") ? "" : "/"}${imagePath}`;
};

const ProfilePersonalInfo: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [countryCode, setCountryCode] = useState<string>("US");
  const { t: myTranslate } = useTranslation();

  // Profile picture states
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);
  const [isDeletingPicture, setIsDeletingPicture] = useState(false);
  const [forceRerender, setForceRerender] = useState(0);

  // Notification states
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const [formData, setFormData] = useState<ProfileFormData>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    city: "",
    country: "",
    addressLine1: "",
  });

  // Helper function to show notifications
  const showNotification = (
    message: string,
    severity: "success" | "error" | "info"
  ) => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const syncFormWithProfile = (data: ProfileData) => {
    const inferredCode = inferCountryCodeFromPhone(data.phoneNumber) || "US";
    const nationalDigits = stripDialCodeFromPhone(
      data.phoneNumber || "",
      inferredCode
    );

    setCountryCode(inferredCode);
    setFormData({
      firstName: alphaOnly(data.firstName || ""),
      lastName: alphaOnly(data.lastName || ""),
      email: data.email || "",
      phoneNumber: trimPhoneToNationalLength(
        inferredCode,
        nationalDigits || ""
      ),
      city: alphaOnly(data.city || ""),
      country: alphaOnly(data.country || ""),
      addressLine1: data.addressLine1 || "",
    });
    setFieldErrors({});
  };

  // Fetch profile data on component mount
  useEffect(() => {
    const fetchProfileData = async () => {
      setIsLoading(true);
      try {
        const response = await getMyProfile();
        if (response.isSuccess && response.data) {
          setProfileData(response.data);
          syncFormWithProfile(response.data);
        }
      } catch (error) {
        console.error("Error fetching profile data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (profileData) {
      syncFormWithProfile(profileData);
    }
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};

    const requiredAlphaFields: Array<{
      key: "firstName" | "lastName";
      label: string;
    }> = [
      { key: "firstName", label: "First name" },
      { key: "lastName", label: "Last name" },
    ];

    requiredAlphaFields.forEach(({ key, label }) => {
      const value = formData[key].trim();
      if (!value) {
        errors[key] = validationMessages.required(label);
      } else if (!isAlphaText(value)) {
        errors[key] = validationMessages.alphaOnly(label);
      }
    });

    const cityValue = formData.city.trim();
    if (cityValue && !isAlphaText(cityValue)) {
      errors.city = validationMessages.alphaOnly("City");
    }

    const countryValue = formData.country.trim();
    if (countryValue && !isAlphaText(countryValue)) {
      errors.country = validationMessages.alphaOnly("Country");
    }

    const emailValue = formData.email.trim();
    if (!emailValue) {
      errors.email = validationMessages.required("Email");
    } else if (!isValidEmail(emailValue)) {
      errors.email = validationMessages.emailInvalid;
    }

    const phoneDigits = sanitizePhoneDigits(formData.phoneNumber);
    if (!phoneDigits) {
      errors.phoneNumber = validationMessages.phoneRequired;
    } else if (requiresStrictNationalLength(countryCode)) {
      const expectedLength = getNationalLengthForCountry(countryCode);
      if (phoneDigits.length !== expectedLength) {
        errors.phoneNumber = validationMessages.phoneLength(expectedLength);
      }
    } else if (phoneDigits.length < 7) {
      errors.phoneNumber = validationMessages.phoneInvalid;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdate = async () => {
    if (!profileData) return;

    if (!validateForm()) {
      showNotification(myTranslate("profile.fixErrorsBeforeUpdating"), "error");
      return;
    }

    const formattedPhone = toE164(countryCode, formData.phoneNumber);
    setIsUpdating(true);
    try {
      const updateData: UpdateProfileRequest = {
        id: profileData.id,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phoneNumber: formattedPhone,
        profilePicture: profileData.profilePicture || "",
        city: formData.city.trim(),
        country: formData.country.trim(),
        region: profileData.region || "",
        addressLine1: formData.addressLine1.trim(),
        addressLine2: profileData.addressLine2 || "",
        postalCode: profileData.postalCode || "",
      };

      const response = await updateProfile(updateData);
      if (response.isSuccess && response.data) {
        // Update local state with new data
        setProfileData(response.data);
        syncFormWithProfile(response.data);
        setIsEditing(false);
        setForceRerender((prev) => prev + 1); // Force complete re-render
        showNotification(myTranslate("profile.profileUpdatedSuccess"), "success");
      } else {
        showNotification(formatApiError(response), "error");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      showNotification(myTranslate("profile.profileUpdateError"), "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleInputChange = (field: keyof ProfileFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleAlphaInput = (
    field: "firstName" | "lastName" | "city" | "country",
    value: string
  ) => {
    handleInputChange(field, alphaOnly(value));
  };

  const handlePhoneChange = (value: string) => {
    const trimmed = trimPhoneToNationalLength(countryCode, value);
    handleInputChange("phoneNumber", trimmed);
  };

  const handleCountrySelect = (country: Country | null) => {
    const code = country?.code ?? "US";
    setCountryCode(code);
    setFormData((prev) => ({
      ...prev,
      phoneNumber: trimPhoneToNationalLength(code, prev.phoneNumber),
    }));

    if (fieldErrors.phoneNumber) {
      setFieldErrors((prev) => ({ ...prev, phoneNumber: "" }));
    }
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        return;
      }

      setIsUploadingPicture(true);
      try {
        const response = await uploadProfilePicture(file);
        if (response.isSuccess && response.data) {
          // Update profile data with new picture URL
          const imageUrl = response.data?.url || "";
          setProfileData((prev) =>
            prev ? { ...prev, profilePicture: imageUrl } : null
          );
          setForceRerender((prev) => prev + 1); // Force complete re-render
          showNotification(myTranslate("profile.profilePictureUploaded"), "success");
        } else {
          showNotification(formatApiError(response), "error");
        }
      } catch (error) {
        console.error("Error uploading profile picture:", error);
        showNotification(myTranslate("profile.profilePictureUploadError"), "error");
      } finally {
        setIsUploadingPicture(false);
      }
    }
  };

  const handleImageDelete = async () => {
    setIsDeletingPicture(true);
    try {
      const response = await deleteProfilePicture();
      if (response.isSuccess) {
        // Update profile data to remove picture
        setProfileData((prev) =>
          prev ? { ...prev, profilePicture: null } : null
        );
        setForceRerender((prev) => prev + 1); // Force complete re-render
        showNotification(myTranslate("profile.profilePictureDeleted"), "success");
      } else {
        showNotification(formatApiError(response), "error");
      }
    } catch (error) {
      console.error("Error deleting profile picture:", error);
      showNotification(myTranslate("profile.profilePictureDeleteError"), "error");
    } finally {
      setIsDeletingPicture(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Typography
        variant="h5"
        className="notranslate"
        translate="no"
        sx={{
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 3,
        }}
      >
        {myTranslate("profile.updateProfile")}
        <HelpOutline sx={{ fontSize: 20, color: "#6b7280" }} />
      </Typography>

      {/* Mobile-First Responsive Layout */}
      <Grid container spacing={{ xs: 2, md: 4 }}>
        {/* Profile Picture Section - Show first on mobile */}
        <Grid item xs={12} md={4} order={{ xs: 1, md: 2 }}>
          <Card sx={{ p: 3, textAlign: "center" }}>
            <Typography
              variant="h6"
              className="notranslate"
              translate="no"
              sx={{ fontWeight: 600, mb: 2 }}
            >
              {myTranslate("profile.profilePicture")}
            </Typography>
            <Box
              key={forceRerender}
              sx={{ position: "relative", display: "inline-block" }}
            >
              <Avatar
                sx={{
                  width: { xs: 100, sm: 120 },
                  height: { xs: 100, sm: 120 },
                  mx: "auto",
                  mb: 2,
                  border: "3px solid #e5e7eb",
                }}
                src={
                  profileData?.profilePicture
                    ? getFullImageUrl(profileData.profilePicture)
                    : "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect width='120' height='120' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='14' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E"
                }
                alt="Profile Picture"
              />

              <input
                accept="image/*"
                hidden
                id="profile-image-upload"
                type="file"
                onChange={handleImageUpload}
                disabled={isUploadingPicture}
              />
              <label htmlFor="profile-image-upload">
                <IconButton
                  component="span"
                  disabled={isUploadingPicture}
                  sx={{
                    position: "absolute",
                    bottom: 8,
                    right: 8,
                    backgroundColor: "#111827",
                    color: "#ffffff",
                    width: { xs: 36, sm: 32 },
                    height: { xs: 36, sm: 32 },
                    "&:hover": {
                      backgroundColor: "#374151",
                    },
                    "&:disabled": {
                      backgroundColor: "#9ca3af",
                    },
                  }}
                >
                  {isUploadingPicture ? (
                    <CircularProgress size={16} sx={{ color: "#ffffff" }} />
                  ) : (
                    <PhotoCamera sx={{ fontSize: { xs: 18, sm: 16 } }} />
                  )}
                </IconButton>
              </label>
              {profileData?.profilePicture && (
                <IconButton
                  onClick={handleImageDelete}
                  disabled={isDeletingPicture}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: "#dc2626",
                    color: "#ffffff",
                    width: { xs: 28, sm: 24 },
                    height: { xs: 28, sm: 24 },
                    "&:hover": {
                      backgroundColor: "#b91c1c",
                    },
                    "&:disabled": {
                      backgroundColor: "#9ca3af",
                    },
                  }}
                >
                  {isDeletingPicture ? (
                    <CircularProgress size={12} sx={{ color: "#ffffff" }} />
                  ) : (
                    "×"
                  )}
                </IconButton>
              )}
            </Box>
            <Typography
              variant="body2"
              className="notranslate"
              translate="no"
              sx={{
                color: "#6b7280",
                mb: 2,
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
              }}
            >
              {myTranslate("profile.profilePictureSize")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                justifyContent: "center",
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Button
                variant="outlined"
                component="label"
                size="small"
                disabled={isUploadingPicture}
                className="notranslate"
                translate="no"
                sx={{
                  textTransform: "none",
                  fontSize: { xs: "0.8rem", sm: "0.75rem" },
                  py: { xs: 1, sm: 0.5 },
                  px: 2,
                  minHeight: { xs: 40, sm: "auto" },
                }}
              >
                {isUploadingPicture
                  ? myTranslate("common.loading")
                  : myTranslate("profile.upload")}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={isUploadingPicture}
                />
              </Button>
              {profileData?.profilePicture && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleImageDelete}
                  disabled={isDeletingPicture}
                  className="notranslate"
                  translate="no"
                  sx={{
                    textTransform: "none",
                    fontSize: { xs: "0.8rem", sm: "0.75rem" },
                    py: { xs: 1, sm: 0.5 },
                    px: 2,
                    minHeight: { xs: 40, sm: "auto" },
                  }}
                >
                  {isDeletingPicture
                    ? myTranslate("common.loading")
                    : myTranslate("profile.delete")}
                </Button>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Form Section - Show second on mobile */}
        <Grid item xs={12} md={8} order={{ xs: 2, md: 1 }}>
          <Card sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Edit Button inside form */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 3,
              }}
            >
              {!isEditing && (
                <Button
                  startIcon={<Edit />}
                  onClick={handleEdit}
                  sx={{
                    color: "#2563eb",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                  }}
                >
                  <span className="notranslate" translate="no">
                    {myTranslate("profile.edit")}
                  </span>
                </Button>
              )}
            </Box>

            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  className="notranslate"
                  translate="no"
                  label={myTranslate("profile.firstName") + "*"}
                  value={formData.firstName}
                  variant="outlined"
                  disabled={!isEditing}
                  onChange={(e) =>
                    handleAlphaInput("firstName", e.target.value)
                  }
                  error={!!fieldErrors.firstName}
                  helperText={fieldErrors.firstName}
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#374151",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  className="notranslate"
                  translate="no"
                  label={myTranslate("profile.lastName") + "*"}
                  value={formData.lastName}
                  variant="outlined"
                  disabled={!isEditing}
                  onChange={(e) => handleAlphaInput("lastName", e.target.value)}
                  error={!!fieldErrors.lastName}
                  helperText={fieldErrors.lastName}
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#374151",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  className="notranslate"
                  translate="no"
                  label={myTranslate("profile.email") + "*"}
                  value={formData.email}
                  variant="outlined"
                  disabled={!isEditing}
                  type="email"
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  error={!!fieldErrors.email}
                  helperText={fieldErrors.email}
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#374151",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  className="notranslate"
                  translate="no"
                  label={myTranslate("profile.phoneNumber")}
                  value={formData.phoneNumber}
                  variant="outlined"
                  disabled={!isEditing}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  error={!!fieldErrors.phoneNumber}
                  helperText={fieldErrors.phoneNumber}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{
                          m: 0,
                          pl: 0,
                          pr: 0.5,
                          alignItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        <CountryCodeSelect
                          value={countryCode}
                          onChange={handleCountrySelect}
                          name="profileCountryCode"
                          compact
                          selectSx={{
                            minWidth: 70,
                            "& .MuiSelect-select": { gap: 0.25, pr: 0 },
                          }}
                          sx={{
                            pointerEvents: isEditing ? "auto" : "none",
                            opacity: isEditing ? 1 : 0.6,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    maxLength: getNationalLengthForCountry(countryCode),
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .MuiInputBase-input": {
                      minWidth: 0,
                      flex: 1,
                      paddingLeft: 0,
                    },
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#374151",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  className="notranslate"
                  translate="no"
                  label={myTranslate("profile.city")}
                  value={formData.city}
                  variant="outlined"
                  disabled={!isEditing}
                  onChange={(e) => handleAlphaInput("city", e.target.value)}
                  error={!!fieldErrors.city}
                  helperText={fieldErrors.city}
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#374151",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  className="notranslate"
                  translate="no"
                  label={myTranslate("profile.country")}
                  value={formData.country}
                  variant="outlined"
                  disabled={!isEditing}
                  onChange={(e) => handleAlphaInput("country", e.target.value)}
                  error={!!fieldErrors.country}
                  helperText={fieldErrors.country}
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#374151",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  className="notranslate"
                  translate="no"
                  label={myTranslate("profile.addressLine1")}
                  value={formData.addressLine1}
                  variant="outlined"
                  disabled={!isEditing}
                  onChange={(e) =>
                    handleInputChange("addressLine1", e.target.value)
                  }
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#374151",
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* Action Buttons - Mobile responsive */}
            {isEditing && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  mt: 3,
                  justifyContent: { xs: "center", sm: "flex-end" },
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Button
                  onClick={handleCancel}
                  className="notranslate"
                  translate="no"
                  sx={{
                    color: "#dc2626",
                    textTransform: "none",
                    fontWeight: 500,
                    minHeight: { xs: 44, sm: "auto" },
                  }}
                >
                  {myTranslate("profile.cancel")}
                </Button>
                <Button
                  onClick={handleUpdate}
                  variant="contained"
                  disabled={isUpdating}
                  className="notranslate"
                  translate="no"
                  sx={{
                    backgroundColor: "#374151",
                    color: "#ffffff",
                    textTransform: "none",
                    fontWeight: 500,
                    px: 3,
                    minHeight: { xs: 44, sm: "auto" },
                    "&:hover": {
                      backgroundColor: "#1f2937",
                    },
                    "&:disabled": {
                      backgroundColor: "#9ca3af",
                      color: "#ffffff",
                    },
                  }}
                >
                  {isUpdating ? (
                    <>
                      <CircularProgress
                        size={16}
                        sx={{ mr: 1, color: "#ffffff" }}
                      />
                      {myTranslate("profile.updating")}
                    </>
                  ) : (
                    myTranslate("profile.updateProfile")
                  )}
                </Button>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfilePersonalInfo;
